import { useEffect, useState } from "react";
import api from "../api";
import { useI18n } from "../i18n";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Settings as SettingsIcon } from "lucide-react";

import { Field } from "../components/FormField";
import { resetPublicDomains } from "../publicDomains";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Languages, Loader2, CheckCircle2, AlertTriangle, MinusCircle, RefreshCw } from "lucide-react";

function DomainStatus({ domain }) {
  const [st, setSt] = useState(null);
  const [loading, setLoading] = useState(false);
  const check = () => {
    if (!domain) { setSt({ status: "not_set" }); return; }
    setLoading(true);
    api.get(`/config/domain-status?domain=${encodeURIComponent(domain)}`)
      .then((r) => setSt(r.data)).catch(() => setSt({ status: "dns_not_configured" }))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(check, 400); return () => clearTimeout(t); }, [domain]);

  const map = {
    active: { label: "Dominio attivo — i link pubblici usano questo dominio", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", Icon: CheckCircle2 },
    not_set: { label: "Non impostato — i link usano il dominio corrente", cls: "text-slate-500 bg-slate-50 border-slate-200", Icon: MinusCircle },
  };
  const info = map[st?.status] || {
    label: "Dominio configurato ma DNS non ancora attivo — attualmente i link usano il dominio Emergent. Passerà automaticamente a questo dominio appena il DNS sarà propagato.",
    cls: "text-amber-700 bg-amber-50 border-amber-200", Icon: AlertTriangle,
  };
  const Icon = info.Icon;
  const host = (u) => (u || "").replace(/^https?:\/\//, "").replace(/\/+$/, "");
  const effective = st?.status === "active" ? host(domain) : window.location.host;
  return (
    <div className="mt-1.5" data-testid="domain-status">
      <div className="flex items-start gap-2">
        <span className={`inline-flex items-start gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs leading-snug ${info.cls}`}>
          {loading ? <Loader2 size={13} className="mt-0.5 shrink-0 animate-spin" /> : <Icon size={13} className="mt-0.5 shrink-0" />}
          <span>{loading ? "Verifica in corso…" : info.label}</span>
        </span>
        <button type="button" onClick={check} className="mt-1 shrink-0 text-slate-400 hover:text-slate-700" title="Verifica di nuovo" data-testid="domain-status-refresh"><RefreshCw size={13} /></button>
      </div>
      <p className="mt-1 pl-0.5 text-[11px] text-slate-500" data-testid="domain-effective">
        Dominio attualmente utilizzato: <span className={`font-semibold ${st?.status === "active" ? "text-emerald-700" : "text-slate-700"}`}>{effective}</span>
      </p>
    </div>
  );
}

function TranslationEngineCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/system/translation-config").then((r) => setCfg(r.data)); }, []);
  if (!cfg) return null;
  const modelKey = `${cfg.provider}::${cfg.model}`;
  const toggleLang = (code, on) => {
    setCfg((c) => ({ ...c, languages: c.languages.map((l) => l.code === code ? { ...l, enabled: on } : l) }));
  };
  const save = async () => {
    setSaving(true);
    try {
      const [provider, model] = modelKey.split("::");
      const enabled_languages = cfg.languages.filter((l) => l.enabled || l.base).map((l) => l.code);
      const r = await api.put("/system/translation-config", { provider, model, enabled_languages });
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success("Motore di traduzione aggiornato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };
  return (
    <Card className="max-w-2xl border-border p-6" data-testid="translation-engine-card">
      <div className="mb-2 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><Languages size={18} /></div>
        <h3 className="font-head text-lg font-semibold text-slate-900">Motore di Traduzione AI</h3>
      </div>
      <p className="mb-5 text-xs text-slate-400">L'operatore scrive i contenuti una sola volta in <b>italiano</b>; Urban OS li traduce automaticamente nelle lingue abilitate. Il modello AI è configurabile: cambiandolo qui, tutta la piattaforma (Welcome Center, guida, contenuti, email) usa il nuovo modello.</p>
      <Field label="Modello AI (unico per tutta la piattaforma)">
        <Select value={modelKey} onValueChange={(v) => setCfg((c) => { const [p, m] = v.split("::"); return { ...c, provider: p, model: m }; })}>
          <SelectTrigger data-testid="ai-model-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {cfg.model_options?.map((o) => <SelectItem key={`${o.provider}::${o.model}`} value={`${o.provider}::${o.model}`}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </Field>
      <div className="mt-5 mb-2 text-sm font-medium text-slate-700">Lingue abilitate</div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        {cfg.languages.map((l) => (
          <label key={l.code} data-testid={`lang-toggle-${l.code}`} className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${l.base ? "border-slate-100 bg-slate-50" : "border-slate-200"}`}>
            <span className="flex items-center gap-2"><span>{l.flag}</span> {l.label}{l.base && <span className="text-[10px] text-slate-400">(sorgente)</span>}</span>
            <Switch checked={l.enabled} disabled={l.base} onCheckedChange={(v) => toggleLang(l.code, v)} />
          </label>
        ))}
      </div>
      <div className="mt-6 flex justify-end">
        <Button data-testid="save-translation-config" onClick={save} disabled={saving} className="bg-primary hover:bg-primary/90">
          {saving && <Loader2 size={14} className="mr-1.5 animate-spin" />}Salva motore AI
        </Button>
      </div>
    </Card>
  );
}

export default function Settings() {
  const { t } = useI18n();
  const [form, setForm] = useState(null);

  useEffect(() => { api.get("/settings").then((r) => setForm(r.data)); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    try { await api.put("/settings", form); resetPublicDomains(); toast.success(t("saved")); }
    catch { toast.error("Error"); }
  };

  if (!form) return <div className="text-slate-400">…</div>;

  return (
    <div className="space-y-6 fade-in" data-testid="settings-page">
      <TranslationEngineCard />
      <Card className="max-w-2xl border-border p-6">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary"><SettingsIcon size={18} /></div>
          <h3 className="font-head text-lg font-semibold text-slate-900">{t("generalSettings")}</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t("company")}><Input data-testid="settings-company" value={form.company_name} onChange={(e) => set("company_name", e.target.value)} /></Field>
          <Field label={t("city")}><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
          <Field label={t("currency")}><Input value={form.currency} onChange={(e) => set("currency", e.target.value)} /></Field>
          <Field label={t("cityTaxDefault")}><Input type="number" value={form.city_tax_default} onChange={(e) => set("city_tax_default", +e.target.value)} /></Field>
          <Field label={t("cleaningCost")}><Input data-testid="settings-cleaning-cost" type="number" value={form.cleaning_cost} onChange={(e) => set("cleaning_cost", +e.target.value)} /></Field>
          <Field label={t("contactEmail")}><Input value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
          <Field label={t("contactPhone")}><Input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
        </div>
        <div className="mt-8 mb-4 flex items-center gap-2 border-t border-slate-200 pt-6">
          <h3 className="font-head text-base font-semibold text-slate-900">{t("commissionRates")}</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label={t("commAirbnb")}><Input data-testid="settings-comm-airbnb" type="number" value={form.commission_airbnb ?? 0} onChange={(e) => set("commission_airbnb", +e.target.value)} /></Field>
          <Field label={t("commBooking")}><Input data-testid="settings-comm-booking" type="number" value={form.commission_booking ?? 0} onChange={(e) => set("commission_booking", +e.target.value)} /></Field>
          <Field label={t("commDiretto")}><Input type="number" value={form.commission_diretto ?? 0} onChange={(e) => set("commission_diretto", +e.target.value)} /></Field>
          <Field label={t("commAltro")}><Input type="number" value={form.commission_altro ?? 0} onChange={(e) => set("commission_altro", +e.target.value)} /></Field>
          <Field label={t("stripeFee")}><Input data-testid="settings-stripe-fee" type="number" value={form.stripe_fee_pct ?? 0} onChange={(e) => set("stripe_fee_pct", +e.target.value)} /></Field>
        </div>
        <div className="mt-8 mb-4 flex items-center gap-2 border-t border-slate-200 pt-6">
          <h3 className="font-head text-base font-semibold text-slate-900">Comunicazioni ospiti & link</h3>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Numero WhatsApp aziendale (futuro Business API)"><Input data-testid="settings-whatsapp-number" placeholder="+39…" value={form.whatsapp_company_number ?? ""} onChange={(e) => set("whatsapp_company_number", e.target.value)} /></Field>
          <Field label="URL Tally documenti (globale)"><Input data-testid="settings-tally-url" placeholder="https://tally.so/r/…" value={form.tally_documents_url ?? ""} onChange={(e) => set("tally_documents_url", e.target.value)} /></Field>
          <Field label="URL Welcome Guide (globale)"><Input data-testid="settings-welcome-url" placeholder="https://…" value={form.welcome_guide_url ?? ""} onChange={(e) => set("welcome_guide_url", e.target.value)} /></Field>
          <Field label="URL Video (globale)"><Input data-testid="settings-video-url" placeholder="https://…" value={form.video_url ?? ""} onChange={(e) => set("video_url", e.target.value)} /></Field>
          <Field label="URL 'Scopri gli appartamenti' (sito Urban Dreams Home)"><Input data-testid="settings-discover-url" placeholder="https://urbandreamshome.it/appartamenti" value={form.discover_apartments_url ?? ""} onChange={(e) => set("discover_apartments_url", e.target.value)} /></Field>
          <Field label="Token webhook Make (Tally → documenti ospiti)"><Input data-testid="settings-make-token" placeholder="token segreto per l'header X-Webhook-Token" value={form.make_webhook_token ?? ""} onChange={(e) => set("make_webhook_token", e.target.value)} /></Field>
        </div>
        <div className="mt-6 rounded-xl border border-indigo-200 bg-indigo-50/50 p-4">
          <h4 className="mb-1 text-sm font-semibold text-indigo-800">Domini pubblici brandizzati (ospite)</h4>
          <p className="mb-3 text-xs text-slate-500">Dominio visibile all'ospite in tutti i link pubblici (check-in, Welcome Guide/QR, Guest Portal). Lascia vuoto per usare il dominio corrente finché il DNS non è attivo. Gli identificativi (UDH…, codici QR e prenotazione) e la logica restano invariati.</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <Field label="Guest Domain (check-in, /g/, /p/)"><Input data-testid="settings-guest-domain" placeholder="https://guest.urbandreamshome.it" value={form.guest_domain ?? ""} onChange={(e) => set("guest_domain", e.target.value.trim())} /></Field>
              <DomainStatus domain={form.guest_domain} />
            </div>
            <div>
              <Field label="Tax Domain (tassa di soggiorno — predisposto)"><Input data-testid="settings-tax-domain" placeholder="https://tax.urbandreamshome.it" value={form.tax_domain ?? ""} onChange={(e) => set("tax_domain", e.target.value.trim())} /></Field>
              <DomainStatus domain={form.tax_domain} />
            </div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4">
          <Field label="Template messaggio check-in (opzionale — se vuoto usa automatico)"><Input data-testid="settings-tpl-checkin" value={form.msg_template_checkin ?? ""} onChange={(e) => set("msg_template_checkin", e.target.value)} /></Field>
          <Field label="Template messaggio Welcome (opzionale)"><Input data-testid="settings-tpl-welcome" value={form.msg_template_welcome ?? ""} onChange={(e) => set("msg_template_welcome", e.target.value)} /></Field>
          <Field label="Template messaggio documenti (opzionale)"><Input data-testid="settings-tpl-docs" value={form.msg_template_documents ?? ""} onChange={(e) => set("msg_template_documents", e.target.value)} /></Field>
        </div>
        <div className="mt-8 mb-4 flex items-center gap-2 border-t border-slate-200 pt-6">
          <h3 className="font-head text-base font-semibold text-slate-900">Check-in pubblico · Mappa Tally per numero di ospiti</h3>
        </div>
        <p className="mb-3 text-xs text-slate-400">Il link pubblico per appartamento è <code>/checkin/&lt;codice&gt;</code>. L'ospite sceglie il numero di ospiti e viene reindirizzato al modulo Tally corrispondente qui sotto (parametri <code>?appartamento=&amp;ospiti=</code> aggiunti automaticamente). Compila solo le righe che ti servono; le altre restano disattivate. Gli URL devono essere in <b>HTTPS</b>.</p>
        <div className="space-y-2" data-testid="settings-tally-mappings">
          {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
            <div key={n} className="grid grid-cols-1 gap-2 sm:grid-cols-[110px_1fr]">
              <div className="flex items-center text-sm font-medium text-slate-600">{n} {n === 1 ? "ospite" : "ospiti"}</div>
              <Input data-testid={`settings-tally-map-${n}`} placeholder="https://tally.so/r/…" value={(form.tally_global_mappings || {})[n] ?? ""}
                onChange={(e) => set("tally_global_mappings", { ...(form.tally_global_mappings || {}), [n]: e.target.value })} />
            </div>
          ))}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4">
          <Field label="Testo benvenuto check-in — IT (opzionale)"><Input data-testid="settings-checkin-welcome-it" placeholder="Lascia vuoto per usare il testo predefinito" value={form.checkin_welcome_it ?? ""} onChange={(e) => set("checkin_welcome_it", e.target.value)} /></Field>
          <Field label="Testo benvenuto check-in — EN (opzionale)"><Input data-testid="settings-checkin-welcome-en" value={form.checkin_welcome_en ?? ""} onChange={(e) => set("checkin_welcome_en", e.target.value)} /></Field>
          <Field label="Testo benvenuto check-in — ES (opzionale)"><Input data-testid="settings-checkin-welcome-es" value={form.checkin_welcome_es ?? ""} onChange={(e) => set("checkin_welcome_es", e.target.value)} /></Field>
        </div>
        <div className="mt-6 flex justify-end">
          <Button data-testid="save-settings-btn" onClick={save} className="bg-primary hover:bg-primary/90">{t("save")}</Button>
        </div>
      </Card>
    </div>
  );
}
